/**
 * Order ring playback. Uses an HTMLAudioElement at full volume; for new
 * orders the chime loops natively until the user interacts or a hard cap
 * fires. Browsers gate `Audio.play()` until there's been a user gesture, so
 * two entry points cover both cases:
 *  - `primeNotificationSound()` runs from the "Enable sound" button: plays
 *    one chime as confirmation and unlocks the element for later
 *    programmatic plays. Resolves true on success, false if the browser
 *    blocked it.
 *  - `playNotificationSound({ onPlayed })` runs from the SSE listener for
 *    `ORDER_PLACED`. Loops the chime; stops on any pointerdown/keydown or
 *    after `MAX_RING_MS`. If the audio element is still locked (no prior
 *    gesture), installs a one-shot gesture listener that starts the ring on
 *    the next user interaction.
 */

const SOUND_URL = '/sounds/notification.mp3';
const MAX_RING_MS = 20_000;
const VOLUME = 1.0;

let audioEl: HTMLAudioElement | null = null;
let activeStop: (() => void) | null = null;
let gestureHandler: ((e: Event) => void) | null = null;
let gestureCallback: (() => void) | null = null;

function getAudio(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  if (!audioEl) {
    audioEl = new Audio(SOUND_URL);
    audioEl.preload = 'auto';
    audioEl.volume = VOLUME;
  }
  return audioEl;
}

function stopActive(): void {
  if (activeStop) {
    activeStop();
    activeStop = null;
  }
}

/**
 * Arm the auto-stop timer and gesture-to-stop listeners for an already
 * playing looped ring. Returns the stop function for callers that need it.
 */
function armStopMachinery(el: HTMLAudioElement): () => void {
  const stop = () => {
    el.loop = false;
    el.pause();
    el.currentTime = 0;
    window.clearTimeout(timeoutId);
    window.removeEventListener('pointerdown', onUserGesture, true);
    window.removeEventListener('keydown', onUserGesture, true);
    if (activeStop === stop) activeStop = null;
  };
  const onUserGesture = () => stop();
  const timeoutId = window.setTimeout(stop, MAX_RING_MS);
  window.addEventListener('pointerdown', onUserGesture, { capture: true, passive: true });
  window.addEventListener('keydown', onUserGesture, { capture: true, passive: true });
  activeStop = stop;
  return stop;
}

function removeGestureHandler(): void {
  if (!gestureHandler) return;
  window.removeEventListener('pointerdown', gestureHandler, true);
  window.removeEventListener('keydown', gestureHandler, true);
  gestureHandler = null;
  gestureCallback = null;
}

function installGestureHandler(onPlayed: () => void): void {
  // Keep only the latest callback — if "Enable sound" beats us to it, that
  // path unlocks and fires its own onPlayed.
  gestureCallback = onPlayed;
  if (gestureHandler) return;
  gestureHandler = async () => {
    const el = getAudio();
    if (!el) return;
    try {
      el.loop = true;
      el.volume = VOLUME;
      el.currentTime = 0;
      await el.play();
      stopActive();
      armStopMachinery(el);
      const cb = gestureCallback;
      removeGestureHandler();
      cb?.();
    } catch {
      // Still locked (rare on a real gesture). Leave handler installed for
      // the next gesture.
    }
  };
  window.addEventListener('pointerdown', gestureHandler, { capture: true, passive: true });
  window.addEventListener('keydown', gestureHandler, { capture: true, passive: true });
}

/**
 * Call from inside a user-gesture handler. Plays one chime as confirmation
 * and unlocks the element for subsequent programmatic plays.
 */
export async function primeNotificationSound(): Promise<boolean> {
  const el = getAudio();
  if (!el) return false;
  try {
    el.loop = false;
    el.currentTime = 0;
    el.volume = VOLUME;
    await el.play();
    const cb = gestureCallback;
    removeGestureHandler();
    cb?.();
    return true;
  } catch {
    return false;
  }
}

export interface PlayNotificationSoundOptions {
  /**
   * Invoked once when the ring actually starts — either immediately, or
   * later (after the next user gesture fires the fallback). The stream hook
   * uses it to mark `soundUnlocked: true` on the store.
   */
  onPlayed?: () => void;
}

/**
 * Start the looping order ring now. If the browser rejects the play (no
 * prior user gesture), install a one-shot capture listener that starts the
 * ring on next interaction so an order that arrived before the user
 * interacted still rings when they click something.
 */
/**
 * Ring continuously until the returned stop() is called. No gesture-stop,
 * no timeout. Used by the order alert dialog which must ring until the
 * owner explicitly accepts or rejects.
 */
export function ringUntilAcknowledged(): () => void {
  const el = getAudio();
  if (!el) return () => {};
  stopActive(); // silence any in-progress notification ring
  el.loop = true;
  el.volume = VOLUME;
  el.currentTime = 0;
  const stop = () => {
    el.loop = false;
    el.pause();
    el.currentTime = 0;
  };
  const playPromise = el.play();
  if (playPromise && typeof playPromise.then === 'function') {
    playPromise.catch(() => {
      // Autoplay blocked — ring on next user gesture (the dialog itself counts).
      const resume = () => {
        el.loop = true;
        el.volume = VOLUME;
        el.currentTime = 0;
        el.play().catch(() => {});
        window.removeEventListener('pointerdown', resume, true);
      };
      window.addEventListener('pointerdown', resume, { capture: true, passive: true });
    });
  }
  return stop;
}

export function playNotificationSound(options: PlayNotificationSoundOptions = {}): void {
  const el = getAudio();
  if (!el) return;
  const onPlayed = options.onPlayed;
  stopActive();
  el.loop = true;
  el.volume = VOLUME;
  el.currentTime = 0;
  const playPromise = el.play();
  if (playPromise && typeof playPromise.then === 'function') {
    playPromise
      .then(() => {
        armStopMachinery(el);
        onPlayed?.();
      })
      .catch(() => {
        if (onPlayed) installGestureHandler(onPlayed);
      });
  } else {
    armStopMachinery(el);
    onPlayed?.();
  }
}
